"use client";

import * as React from "react";
import Link from "next/link";
import { useSearchParams } from "next/navigation";
import { Check, Gift, Loader2, Lock, Mail, User } from "lucide-react";
import { motion } from "framer-motion";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/layout/logo";
import { api } from "@/lib/api";
import { browserAuth } from "@/lib/auth-client";
import { cn } from "@/lib/utils";

export default function RegisterPage() {
  const searchParams = useSearchParams();
  const refCode = searchParams.get("ref")?.toUpperCase() || "";
  const [email, setEmail] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [name, setName] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [referralValid, setReferralValid] = React.useState<boolean | null>(null);
  const [emailSent, setEmailSent] = React.useState(false);
  const [googleAvailable, setGoogleAvailable] = React.useState(false);
  const [googleLoading, setGoogleLoading] = React.useState(false);

  React.useEffect(() => {
    let active = true;
    browserAuth.googleStatus()
      .then(({ available }) => {
        if (active) setGoogleAvailable(available === true);
      })
      .catch(() => {
        if (active) setGoogleAvailable(false);
      });
    return () => {
      active = false;
    };
  }, []);

  // M47b: Sprawdź czy kod referral jest valid (UX)
  React.useEffect(() => {
    if (!refCode) {
      setReferralValid(null);
      return;
    }
    (async () => {
      try {
        const r = await api.get<{ valid: boolean }>(
          `/referral/check/${encodeURIComponent(refCode)}`,
        );
        setReferralValid(r.data.valid);
      } catch {
        setReferralValid(false);
      }
    })();
  }, [refCode]);

  const checks = {
    length: password.length >= 12,
    lower: /[a-z]/.test(password),
    upper: /[A-Z]/.test(password),
    digit: /\d/.test(password),
    special: /[^A-Za-z0-9]/.test(password),
    match: password.length > 0 && password === confirm,
  };
  const classCount = [checks.lower, checks.upper, checks.digit, checks.special].filter(Boolean).length;
  const allValid = checks.length && classCount >= 3 && checks.match;

  // Nowy e-mail z Google zakłada konto w Auth; powrót prowadzi do onboardingu.
  const onGoogleRegister = async () => {
    setGoogleLoading(true);
    try {
      const { authorization_url: authorizationUrl } = await browserAuth.googleStart("onboarding");
      const target = new URL(authorizationUrl);
      if (
        target.origin !== "https://accounts.google.com" ||
        target.pathname !== "/o/oauth2/v2/auth"
      ) {
        throw new Error("Niepoprawny adres logowania Google");
      }
      window.location.assign(target.href);
    } catch (error) {
      toast.error("Rejestracja przez Google jest niedostępna", {
        description: error instanceof Error ? error.message : undefined,
      });
      setGoogleLoading(false);
    }
  };

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid) {
      toast.error("Sprawdź wymagania hasła");
      return;
    }
    setLoading(true);
    try {
      await browserAuth.register({
        email,
        password,
        name: name || undefined,
        referral_code: referralValid && refCode ? refCode : undefined,
      });
      setEmailSent(true);
      toast.success("Sprawdź email", {
        description: "Jeśli konto może zostać utworzone, wysłaliśmy link aktywacyjny",
      });
    } catch (err) {
      const detail =
        (err as { message?: string })?.message ||
        (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        "Spróbuj ponownie";
      toast.error("Rejestracja nieudana", { description: detail });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center bg-background p-4">
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[linear-gradient(to_right,oklch(0.92_0_0)/0.4_1px,transparent_1px),linear-gradient(to_bottom,oklch(0.92_0_0)/0.4_1px,transparent_1px)] bg-[size:48px_48px] dark:bg-[linear-gradient(to_right,oklch(0.27_0_0)/0.3_1px,transparent_1px),linear-gradient(to_bottom,oklch(0.27_0_0)/0.3_1px,transparent_1px)]"
      />
      <div
        aria-hidden
        className="absolute inset-0 -z-10 bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,oklch(0.96_0.04_38),transparent_70%)] dark:bg-[radial-gradient(ellipse_60%_50%_at_50%_-10%,oklch(0.27_0.08_38)/0.5,transparent_70%)]"
      />

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
        className="w-full max-w-md"
      >
        <Card className="border-border/60 backdrop-blur-sm">
          <CardContent className="space-y-6 p-8">
            <div className="flex flex-col items-center gap-3 text-center">
              <Logo size="lg" showText={false} />
              <h1 className="text-2xl font-semibold tracking-tight">
                {emailSent ? "Sprawdź email" : "Załóż konto"}
              </h1>
              <p className="text-sm text-muted-foreground">
                {emailSent
                  ? `Wysłaliśmy link aktywacyjny na ${email}. Kliknij go żeby aktywować konto.`
                  : "Bezpłatny trial · Połączysz Allegro w następnym kroku"}
              </p>
            </div>

            {emailSent && (
              <div className="text-center text-sm">
                <p className="text-muted-foreground">
                  Nie dostałeś emaila? Sprawdź spam lub{" "}
                  <button
                    onClick={() => setEmailSent(false)}
                    className="font-medium text-primary hover:underline"
                  >
                    spróbuj ponownie
                  </button>
                </p>
              </div>
            )}

            {/* M47b: Referral code banner */}
            {refCode && (
              <div className={cn(
                "flex items-center gap-2 rounded-md border-2 p-2 text-xs",
                referralValid === true && "border-green-500/40 bg-green-500/[0.04] text-green-700",
                referralValid === false && "border-red-500/40 bg-red-500/[0.04] text-red-700",
                referralValid === null && "border-muted text-muted-foreground"
              )}>
                <Gift className="h-4 w-4 shrink-0" />
                <div className="flex-1">
                  {referralValid === true && (
                    <>Polecenie: <b className="font-mono">{refCode}</b> ✓</>
                  )}
                  {referralValid === false && (
                    <>Kod <b className="font-mono">{refCode}</b> nie istnieje — rejestracja bez polecenia</>
                  )}
                  {referralValid === null && (
                    <>Sprawdzam kod <b className="font-mono">{refCode}</b>...</>
                  )}
                </div>
              </div>
            )}

            {!emailSent && (
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="ty@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Nazwa (opcjonalnie)</Label>
                <div className="relative">
                  <User className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="name"
                    type="text"
                    placeholder="Jak Cię nazywać?"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Hasło</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirm">Powtórz hasło</Label>
                <div className="relative">
                  <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    id="confirm"
                    type="password"
                    autoComplete="new-password"
                    value={confirm}
                    onChange={(e) => setConfirm(e.target.value)}
                    className="pl-9"
                    required
                  />
                </div>
              </div>

              {/* Password strength */}
              <ul className="space-y-1 text-xs">
                {[
                  { ok: checks.length, label: "Minimum 12 znaków" },
                  { ok: classCount >= 3, label: "3 z 4: małe/duże litery, cyfry, znaki specjalne" },
                  { ok: checks.match, label: "Hasła zgodne" },
                ].map((c) => (
                  <li
                    key={c.label}
                    className={cn(
                      "flex items-center gap-2",
                      c.ok ? "text-success" : "text-muted-foreground"
                    )}
                  >
                    <Check
                      className={cn(
                        "h-3.5 w-3.5",
                        c.ok ? "opacity-100" : "opacity-30"
                      )}
                    />
                    {c.label}
                  </li>
                ))}
              </ul>

              <Button
                type="submit"
                disabled={loading || !allValid}
                size="lg"
                className="w-full"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                Załóż konto
              </Button>
            </form>
            )}

            {!emailSent && googleAvailable && (
              <div className="space-y-3">
                <p className="text-center text-xs text-muted-foreground">lub</p>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  className="w-full"
                  disabled={loading || googleLoading}
                  onClick={() => void onGoogleRegister()}
                >
                  {googleLoading && <Loader2 className="h-4 w-4 animate-spin" />}
                  Załóż konto przez Google
                </Button>
              </div>
            )}

            <p className="text-center text-sm text-muted-foreground">
              Masz już konto?{" "}
              <Link
                href="/login"
                className="font-medium text-primary hover:underline"
              >
                Zaloguj się
              </Link>
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
